<template>
  <v-container class="mt-n5" :fluid="isFluidRange">
    <PageHeader :title="pageTitle" :path="goToPath" />

    <!-- filtros -->
    <v-card class="mt-n5" :loading="!loadedView">
      <v-container
        class="pt-0"
        :class="{ 'pb-0': !showFilters, 'pb-4': showFilters }"
      >
        <v-card-actions>
          <v-row align="start" no-gutters>
            <v-col
              cols="11"
              v-if="filtersApplied && filtersApplied.length > 0 && !showFilters"
            >
              <v-chip
                v-for="(filter, key) in filtersApplied"
                :key="key"
                class="mr-2"
                label
                outlined
                color="primary"
                @click="showFilters = true"
              >
                {{
                  `${filter.label}: ${
                    Array.isArray(filter.model)
                      ? truncateText(getTextWhenFilterIsArray(filter))
                      : truncateText(filter.model)
                  }`
                }}
              </v-chip>
            </v-col>
            <v-col
              cols="11"
              v-if="
                !(filtersApplied && filtersApplied.length > 0 && !showFilters)
              "
            ></v-col>
            <v-col xl="1" lg="1" md="1" sm="1" xs="1">
              <v-tooltip location="right">
                <template v-slot:activator="{ props }">
                  <v-btn
                    :icon="showFilters ? upArrowIcon : downArrowIcon"
                    @click="showFilters = !showFilters"
                    color="grey"
                    v-bind="props"
                    class="mr-5 white--text to-right"
                    size="25px"
                    id="v-btn-showFilters"
                  ></v-btn>
                </template>
                {{
                  showFilters ? "Ocultar filtros" : "Mostrar filtros"
                }}</v-tooltip
              >
            </v-col>
          </v-row>
        </v-card-actions>
        <v-expand-transition v-if="loadedView">
          <v-form
            v-show="showFilters"
            v-model="isFormValid"
            ref="filters-form"
            id="filters-form"
            @submit.prevent="loadSolicitudes()"
          >
            <v-row :no-gutters="isMobile">
              <v-col
                cols="12"
                :xl="isCuirInput ? 5 : 2"
                :lg="isCuirInput ? 5 : 3"
                :md="isCuirInput ? 7 : (isUserFarmacia ? 4 : 6)"
                :sm="6"
                xs="12"
                class="pb-0"
              >
                <v-text-field
                  label="Número solicitud o CUIR"
                  v-model="numeroSolicitud"
                  autocomplete="solicitud"
                  clearable
                  variant="outlined"
                  density="compact"
                  onkeypress="return (event.charCode >= 48 && event.charCode <= 57)"
                  :rules="[
                    rules.maxLength(numeroSolicitud, 100),
                    rules.numeric,
                  ]"
                  @update:model-value="changeNumeroSolicitud"
                  @keydown.tab="
                    numeroSolicitud
                      ? focusComponent('vBtnAplicarFiltros', 1)
                      : null
                  "
                  id="v-text-field-nroSolicitud"
                ></v-text-field>
              </v-col>
              <v-col
                cols="12"
                xl="4"
                :lg="3"
                :md="isUserFarmacia ? 4 : 6"
                :sm="6"
                xs="12"
                class="pb-0"
              >
                <v-autocomplete
                  label="Afiliado"
                  v-model="selectedAfiliado"
                  autocomplete="off"
                  :items="afiliados"
                  variant="outlined"
                  density="compact"
                  v-model:search="inputAfiliado"
                  return-object
                  item-title="value3"
                  item-value="value1"
                  hint="Búsqueda por CUIL, DNI o N° afiliado"
                  persistent-hint
                  :custom-filter="customFilter"
                  :loading="loadingAfiliados"
                  clearable
                  :no-data-text="afiliadosNoDataText"
                  :disabled="disabledByNroSolicitud"
                  :readonly="selectedAfiliado != null"
                  :menu-props="{ closeOnContentClick: true }"
                  id="v-autocomplete-afiliado"
                >
                  <template v-slot:item="{ item }">
                    <v-list-item @click="setAfiliado(item)">
                      <v-list-item-title>{{ item.title }}</v-list-item-title>
                      <v-list-item-subtitle
                        >N° afiliado: {{ item.value }}
                      </v-list-item-subtitle>
                      <v-list-item-subtitle
                        >CUIL: {{ item.raw.value4 }}
                      </v-list-item-subtitle>
                      <v-list-item-subtitle
                        >DNI: {{ item.raw.value2 }}
                      </v-list-item-subtitle>
                    </v-list-item>
                  </template>
                </v-autocomplete>
              </v-col>
              <v-col
                cols="12"
                xl="3"
                :lg="3"
                :md="isUserFarmacia ? 4 : 6"
                :sm="6"
                xs="12"
                class="pb-0"
                v-if="!isUserFarmacia"
              >
                <v-autocomplete
                  label="Financiador"
                  v-model="selectedFinanciador"
                  autocomplete="off"
                  :items="financiadores"
                  :disabled="disabledByNroSolicitud"
                  variant="outlined"
                  density="compact"
                  return-object
                  item-value="id"
                  item-title="value"
                  :clearable="isClearable('financiador')"
                  :loading="financiadores.length === 0"
                  :readonly="isUserFinanciador"
                  id="v-autocomplete-financiador"
                ></v-autocomplete>
              </v-col>
              <v-col
                cols="12"
                xl="3"
                :lg="3"
                :md="isUserFarmacia ? 4 : 6"
                :sm="6"
                xs="12"
                class="pb-0"
                v-if="!isUserFarmacia && !isUserPrestador"
              >
                <v-autocomplete
                  variant="outlined"
                  density="compact"
                  label="Entidad emisora"
                  autocomplete="off"
                  v-model="selectedEmisor"
                  :disabled="disabledByNroSolicitud"
                  :items="emisores"
                  return-object
                  item-value="id"
                  item-title="value"
                  :clearable="isClearable('emisor')"
                  :loading="emisores.length === 0"
                  :readonly="isUserEmisor"
                  id="v-autocomplete-emisor"
                ></v-autocomplete>
              </v-col>
              <!-- Nuevo filtro unificado de fechas -->
              <v-col cols="12" xl="3" :lg="3" :md="4" :sm="6" xs="12" class="pb-0">
                <v-select
                  v-model="selectedDateFilter"
                  :items="availableDateFilters"
                  item-title="text"
                  item-value="value"
                  label="Filtrar por fecha de"
                  :disabled="disabledByNroSolicitud || availableDateFilters.length === 0"
                  variant="outlined"
                  density="compact"
                />
              </v-col>

              <v-col cols="12" xl="2" :lg="isUserFarmacia ? 3 : 3" :md="isUserFarmacia ? 4 : 6" :sm="isUserFarmacia ? 6 : 6" xs="12" class="pb-0">
                <DatePicker
                  @selectFecha="selectedDateFrom = $event"
                  :label="`Fecha ${labelActual} desde`"
                  :minDate="minDate"
                  :disabled="disabledByNroSolicitud"
                  :clearable="true"
                  :dateMenu="menuFechaDesde"
                  :defaultDate="selectedDateFilter === 'fechaCreacion' ? fechaCreacionDesde : selectedDateFilter === 'fechaDispensa' ? fechaDispensaDesde : fechaDesde"
                  :rules="selectedDateFilter ? ([...rules.validDateFormat, rules.validDateRange(selectedDateFrom, selectedDateTo)]) : []"
                />
              </v-col>

              <v-col cols="12" xl="2" :lg="isUserFarmacia ? 3 : 3" :md="isUserFarmacia ? 4 : 6" :sm="isUserFarmacia ? 6 : 6" xs="12" class="pb-0">
                <DatePicker
                  @selectFecha="selectedDateTo = $event"
                  :label="`Fecha ${labelActual} hasta`"
                  :minDate="minDate"
                  :disabled="disabledByNroSolicitud"
                  :clearable="true"
                  :dateMenu="menuFechaHasta"
                  :defaultDate="selectedDateFilter === 'fechaCreacion' ? fechaCreacionHasta : selectedDateFilter === 'fechaDispensa' ? fechaDispensaHasta : fechaHasta"
                  :rules="selectedDateFilter ? ([...rules.validDateFormat, rules.validDateRange(selectedDateFrom, selectedDateTo)]) : []"
                />
              </v-col>
              <!-- Tipos de prescripción -->
              <v-col
                cols="12"
                xl="3"
                :lg="3"
                :md="6"
                :sm="6"
                xs="12"
                class="pb-0"
              >
                <v-autocomplete
                  label="Tipo de prescripción"
                  v-model="selectedTiposSolicitudes"
                  autocomplete="off"
                  :items="tiposSolicitudes"
                  :disabled="disabledByNroSolicitud"
                  variant="outlined"
                  density="compact"
                  multiple
                  return-object
                  item-value="id"
                  item-title="value"
                  clearable
                  id="v-autocomplete-tipos-solicitudes"
                >
                  <template v-slot:selection="{ item, index }">
                    <v-chip v-if="index < 1">
                      <span>{{ item.title }}</span>
                    </v-chip>
                    <span
                      v-if="index === 1"
                      class="text-grey text-caption align-self-center"
                    >
                      (+{{ selectedTiposSolicitudes.length - 1 }} otros)
                    </span>
                  </template>
                </v-autocomplete>
              </v-col>
              <v-col
                cols="12"
                xl="3"
                :lg="isUserFarmacia ? 3 : 3"
                :md="isUserFarmacia ? 4 : 6"
                :sm="isUserFarmacia ? 6 : 6"
                xs="12"
                class="pb-0"
                v-if="!isUserFarmacia && !isUserPrestador"
              >
                <v-autocomplete
                  label="Estado/s"
                  v-model="selectedEstados"
                  autocomplete="off"
                  :items="estadosList"
                  :disabled="disabledByNroSolicitud"
                  variant="outlined"
                  density="compact"
                  :loading="estadosList.length === 0"
                  multiple
                  return-object
                  item-value="id"
                  item-title="value"
                  clearable
                  id="v-autocomplete-estadosList"
                >
                  <template v-slot:selection="{ item, index }">
                    <v-chip v-if="index < 1">
                      <span>{{ item.title }}</span>
                    </v-chip>
                    <span
                      v-if="index === 1"
                      class="text-grey text-caption align-self-center"
                    >
                      (+{{ selectedEstados.length - 1 }} otros)
                    </span>
                  </template></v-autocomplete
                >
              </v-col>
              <!-- Filtro de Farmacias - Solo para usuarios financiadores -->
              <v-col
                cols="12"
                xl="3"
                :lg="3"
                :md="6"
                :sm="6"
                xs="12"
                class="pb-0"
                v-if="isUserFinanciador || esAdmin"
              >
                <v-autocomplete
                  label="Farmacia"
                  v-model="selectedFarmacia"
                  autocomplete="off"
                  :items="farmacias"
                  :disabled="disabledByNroSolicitud"
                  variant="outlined"
                  density="compact"
                  return-object
                  item-value="id"
                  item-title="value"
                  :clearable="true"
                  :loading="loadingFarmacias"
                  id="v-autocomplete-farmacia"
                ></v-autocomplete>
              </v-col>
            </v-row>
            <v-row class="text-right py-0" no-gutters>
              <v-col cols="12">
                <v-tooltip location="left">
                  <template v-slot:activator="{ props }">
                    <v-icon
                      color="grey"
                      v-bind="props"
                      class="mr-5 white--text"
                      size="25px"
                      @click="clearFilters"
                      id="v-icon-limpiarFiltros"
                      >{{ clearFiltersIcon }}</v-icon
                    >
                  </template>
                  Limpiar filtros</v-tooltip
                >
                <v-btn
                  color="primary"
                  type="submit"
                  :disabled="isAplicarButtonDisabled"
                  form="filters-form"
                  id="vBtnAplicarFiltros"
                >
                  Aplicar
                </v-btn>
              </v-col>
            </v-row>
          </v-form>
        </v-expand-transition>
      </v-container>
    </v-card>

    <!-- data table -->
    <v-card class="elevation-1 mt-2">
      <v-card-text class="py-0 my-0 px-0">
        <v-row no-gutters>
          <v-col cols="12" class="mx-0 px-0">
            <v-data-table
              :headers="headersTable"
              :items="solicitudes"
              :class="['pa-2', { 'table-compact': isFluidRange }]"
              :search="search"
              :loading="isLoadingTable"
              :mobile="isMobile"
              :hide-default-header="isMobile"
              :items-per-page="isMobile ? 5 : 10"
              show-current-page
              :items-per-page-options="
                isMobile
                  ? [
                      { value: 5, title: '5' },
                      { value: 10, title: '10' },
                    ]
                  : [
                      { value: 10, title: '10' },
                      { value: 25, title: '25' },
                      { value: 50, title: '50' },
                      { value: 100, title: '100' },
                      {
                        value: -1,
                        title: '$vuetify.dataFooter.itemsPerPageAll',
                      },
                    ]
              "
            >
              <template v-slot:top>
                <v-toolbar flat style="background-color: transparent">
                  <v-row>
                    <v-col
                      :cols="isMobile ? 8 : 8"
                      align-self="center"
                      class="buscador pl-0 pt-0"
                    >
                      <v-text-field
                        v-model="search"
                        :append-inner-icon="searchIcon"
                        label="Buscar"
                        single-line
                        variant="underlined"
                        hide-details
                        clearable
                        class="ml-5"
                        style="width: auto"
                        id="v-text-field-search"
                      ></v-text-field>
                    </v-col>
                    <v-col
                      :cols="isMobile ? 4 : 4"
                      v-if="
                        permisosSolicitudes.canCreateReceta && isUserPrestador
                      "
                      :style="
                        isMobile
                          ? 'display: flex; flex-wrap: wrap; justify-content: center;'
                          : ''
                      "
                    >
                      <v-menu transition="scale-transition" location="bottom">
                        <template v-slot:activator="{ props }">
                          <v-btn
                            color="primary"
                            v-bind="props"
                            variant="flat"
                            :class="isMobile ? '' : 'to-right'"
                          >
                            Nueva
                          </v-btn>
                        </template>
                        <v-list>
                          <v-list-item
                            v-for="item in createOptions"
                            :key="item.id"
                            link
                            density="compact"
                            @click="openModalCreateSolicitud(item.id)"
                          >
                            <v-list-item-title>{{
                              item.title
                            }}</v-list-item-title>
                          </v-list-item>
                        </v-list>
                      </v-menu>
                    </v-col>
                  </v-row>
                </v-toolbar>
              </template>
              <template v-slot:[`item.emisor.value`]="{ item }">
                <span>
                  {{
                    this.isUserPrestador
                      ? `${item.nroMatricula} ${item.emisor.value}`
                      : item.emisor.value
                  }}
                </span>
              </template>
              <template v-slot:[`item.cuir`]="{ item }">
                <div class="d-flex align-center" :class="isMobile ? 'justify-end' : 'justify-center'">
                  <!-- Eye icon for viewing CUIR details - only if CUIR exists -->
                  <template v-if="item.cuirSolicitud">
                    <v-menu
                      v-model="tooltipStates[item.id]"
                      :close-on-content-click="false"
                      :close-on-back="false"
                      persistent
                      location="top"
                      offset="10"
                    >
                      <template v-slot:activator="{ props }">
                        <v-tooltip location="left">
                          <template v-slot:activator="{ props: tooltipProps }">
                            <v-icon
                              v-bind="{ ...props, ...tooltipProps }"
                              icon="mdi-eye"
                              size="20"
                              color="primary"
                              class="cursor-pointer"
                              @click="loadItemsCuir(item)"
                            />
                          </template>
                          <span>Ver CUIR completo</span>
                        </v-tooltip>
                      </template>
                      
                      <!-- Content card inside menu -->
                      <v-card
                        :min-width="isMobile ? '280' : '500'"
                        :max-width="isMobile ? '340' : '600'"
                        class="cuir-details-card"
                        color="grey-darken-4"
                        @click.stop
                      >
                        <!-- Card header with close button -->
                        <v-card-title class="pa-3 pb-2 d-flex justify-space-between align-center">
                          <span :style="`
                            font-size: ${isMobile ? '10px' : '12px'}; 
                            color: #bdc3c7; 
                            text-transform: uppercase;
                            letter-spacing: 0.5px;
                            font-family: 'Courier New', monospace;
                          `">
                            CUIR - Código Único de Identificación
                          </span>
                          <v-btn
                            icon="mdi-close"
                            size="small"
                            variant="text"
                            color="white"
                            @click.stop="closeTooltip(item.id)"
                          />
                        </v-card-title>
                        
                        <!-- Card content -->
                        <v-card-text :class="isMobile ? 'pa-2' : 'pa-4'">
                          <div v-if="loadingItemsCuir[item.id]" class="text-center pa-4">
                            <v-progress-circular indeterminate color="primary" size="24"></v-progress-circular>
                            <div class="mt-2 text-caption">Cargando CUIRs...</div>
                          </div>
                          
                          <div v-else-if="itemsCuirData[item.id] && itemsCuirData[item.id].length > 0">
                            <!-- Lista de CUIRs -->
                            <div class="cuir-list" :class="{ 'cuir-list-scroll': tieneMasQueLimiteCuir(item) }">
                              <div 
                                v-for="(cuirItem, index) in itemsCuirData[item.id]" 
                                :key="index"
                                class="cuir-item-clickable mb-2"
                                @click.stop="copyCuir(cuirItem)"
                                :style="`
                                  font-size: ${isMobile ? '11px' : '14px'}; 
                                  font-weight: 500; 
                                  word-break: break-all;
                                  line-height: 1.3;
                                  background: rgba(255,255,255,0.08);
                                  padding: ${isMobile ? '8px' : '10px'};
                                  border-radius: 4px;
                                  border-left: 3px solid #3498db;
                                  color: white;
                                  font-family: 'Courier New', monospace;
                                  cursor: pointer;
                                  user-select: none;
                                  position: relative;
                                  transition: all 0.2s ease;
                                `"
                                :title="`Click para copiar CUIR ${index + 1}`"
                              >
                                {{ cuirItem }}
                                <v-icon 
                                  v-if="!isMobile"
                                  icon="mdi-content-copy" 
                                  size="x-small" 
                                  color="rgba(255,255,255,0.5)"
                                  style="
                                    position: absolute;
                                    top: 8px;
                                    right: 6px;
                                    opacity: 0.6;
                                  "
                                />
                              </div>
                            </div>
                          </div>
                          
                          <div v-else class="text-center pa-4">
                            <div class="text-caption" style="color: #bdc3c7;">
                              No se encontraron CUIRs de ítems para esta prescripción.
                            </div>
                          </div>
                        </v-card-text>
                      </v-card>
                    </v-menu>
                  </template>
                  
                  <!-- Show dash when no CUIR -->
                  <template v-else>
                    <span style="color: #999; font-size: 14px;">---</span>
                  </template>
                </div>
              </template>
              <template v-slot:[`item.actions`]="{ item }">
                <template v-if="isMobile">
                  <v-menu transition="scale-transition" location="left">
                    <template v-slot:activator="{ props }">
                      <v-btn
                        color="primary"
                        v-bind="props"
                        variant="flat"
                        :class="item ? '' : 'to-right'"
                      >
                        Ver acciones
                      </v-btn>
                    </template>
                    <v-list>
                      <v-list-item
                        v-if="
                          permisosSolicitudes.canCreateReceta && isUserPrestador
                        "
                        link
                        density="compact"
                        @click="copyPrescripcion(item)"
                      >
                        <v-list-item-title>Copiar prescripción</v-list-item-title>
                      </v-list-item>
                      <v-list-item
                        v-if="permisosSolicitudes.canSeeHistory"
                        link
                        density="compact"
                        @click="openModalStatus(item)"
                      >
                        <v-list-item-title
                          >Ver historial estados</v-list-item-title
                        >
                      </v-list-item>
                      <v-list-item
                        v-if="permisosSolicitudes.canSeeLog"
                        link
                        density="compact"
                        @click="openModalVerLog(item.id)"
                      >
                        <v-list-item-title>Ver log</v-list-item-title>
                      </v-list-item>
                      <v-list-item
                        v-if="
                          permisosSolicitudes.canSeeDispensarReceta &&
                          canDispensarReceta(item)
                        "
                        link
                        density="compact"
                        @click="openModalReceta(item)"
                      >
                        <v-list-item-title>Ver dispensa</v-list-item-title>
                      </v-list-item>
                      <v-list-item
                        v-if="
                          (isUserPrestador &&
                            item.estado == ESTADOS_SOLICITUD[1]) ||
                          (isUserEmisor && item.estado != ESTADOS_SOLICITUD[5]) ||
                          (permisosSolicitudes.canSeeCancelApplication &&
                            item.estado != ESTADOS_SOLICITUD[5] &&
                            canAnularReceta(item))
                        "
                        link
                        density="compact"
                        @click="openAnularSolicitudDialog(item)"
                      >
                        <v-list-item-title>Anular receta</v-list-item-title>
                      </v-list-item>
                      <v-list-item
                        v-if="canImprimirReceta(item) || isUserPrestador"
                        link
                        density="compact"
                        @click="imprimirSolicitud(item.numero)"
                      >
                        <v-list-item-title>Imprimir receta</v-list-item-title>
                      </v-list-item>
                      <v-list-item
                        v-if="
                          !(canImprimirReceta(item) || isUserPrestador) &&
                          !(
                            (isUserPrestador &&
                              item.estado == ESTADOS_SOLICITUD[1]) ||
                            (isUserEmisor && item.estado != ESTADOS_SOLICITUD[5]) ||
                            (permisosSolicitudes.canSeeCancelApplication &&
                              item.estado != ESTADOS_SOLICITUD[5] &&
                              canAnularReceta(item))
                          ) &&
                          !(
                            permisosSolicitudes.canSeeDispensarReceta &&
                            canDispensarReceta(item)
                          ) &&
                          !permisosSolicitudes.canSeeLog &&
                          !permisosSolicitudes.canSeeHistory &&
                          !(
                            permisosSolicitudes.canCreateReceta &&
                            isUserPrestador
                          )
                        "
                        link
                        density="compact"
                      >
                        <v-list-item-title
                          >No hay acciones disponibles</v-list-item-title
                        >
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </template>
                <template v-if="!isMobile">
                  <v-tooltip
                    location="left"
                    v-if="
                      permisosSolicitudes.canCreateReceta && isUserPrestador
                    "
                  >
                    <template v-slot:activator="{ props }">
                      <v-icon
                        v-bind="props"
                        size="20"
                        :disabled="loadingCopyReceta"
                        id="v-icon-copiar-receta"
                        @click="copyPrescripcion(item)"
                      >
                        {{ copyIcon }}
                      </v-icon>
                    </template>
                    <span>Copiar prescripción</span>
                  </v-tooltip>
                  <v-tooltip
                    location="left"
                    v-if="permisosSolicitudes.canSeeHistory"
                  >
                    <template v-slot:activator="{ props }">
                      <v-icon
                        v-bind="props"
                        size="20"
                        @keydown.enter="openModalStatus(item)"
                        @click="openModalStatus(item)"
                        id="v-icon-verHistorial"
                      >
                        {{ calendarClockIcon }}
                      </v-icon>
                    </template>
                    <span>Ver historial estados</span>
                  </v-tooltip>
                  <v-tooltip
                    location="left"
                    v-if="permisosSolicitudes.canSeeLog"
                  >
                    <template v-slot:activator="{ props }">
                      <v-icon
                        v-bind="props"
                        size="20"
                        @keydown.enter="openModalVerLog(item.id)"
                        @click="openModalVerLog(item.id)"
                        id="v-icon-verLog"
                      >
                        {{ documentIcon }}
                      </v-icon>
                    </template>
                    <span>Ver log</span>
                  </v-tooltip>
                  <v-tooltip
                    location="left"
                    v-if="
                      permisosSolicitudes.canSeeDispensarReceta &&
                      canDispensarReceta(item)
                    "
                  >
                    <template v-slot:activator="{ props }">
                      <v-icon
                        v-bind="props"
                        size="20"
                        @keydown.enter="openModalReceta(item)"
                        @click="openModalReceta(item)"
                        id="v-icon-dispensarReceta"
                      >
                        {{ editIcon }}
                      </v-icon>
                    </template>
                    <span>Ver dispensa</span>
                  </v-tooltip>
                  <v-tooltip location="left" v-if="canAnularReceta(item)">
                    <template v-slot:activator="{ props }">
                      <v-icon
                        v-bind="props"
                        size="20"
                        @keydown.enter="openAnularSolicitudDialog(item)"
                        @click="openAnularSolicitudDialog(item)"
                        id="v-icon-anularSolicitud"
                      >
                        {{ anularSolicitudIcon }}
                      </v-icon>
                    </template>
                    <span>Anular solicitud</span>
                  </v-tooltip>
                  <v-tooltip
                    location="left"
                    v-if="canImprimirReceta(item)"
                    :loading="loadingPrint"
                  >
                    <template v-slot:activator="{ props }">
                      <v-icon
                        :disabled="loadingPrint"
                        v-bind="props"
                        @keydown.enter="
                          loadingPrint ? null : imprimirSolicitud(item.numero)
                        "
                        size="20"
                        @click="imprimirSolicitud(item.numero)"
                        id="v-icon-imprimirReceta"
                      >
                        {{ imprimirSolicitudIcon }}
                      </v-icon>
                    </template>
                    <span>Imprimir solicitud</span>
                  </v-tooltip>
                </template>
              </template>
            </v-data-table>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
    <v-row>
      <v-col cols="12" class="pt-5">
        <template v-if="(opcionesExportacion || []).length <= 1">
          <v-btn
            color="primary"
            class="to-right"
            variant="outlined"
            :disabled="(solicitudes || []).length === 0"
            @click="exportarConsulta(opcionesExportacion[0] || TIPOS_EXPORTACION.POR_SOLICITUD)"
          >
            Exportar detalle
          </v-btn>
        </template>
        <template v-else>
          <v-menu v-model="exportMenu" location="bottom" :close-on-content-click="true">
            <template v-slot:activator="{ props }">
              <v-btn
                v-bind="props"
                color="primary"
                class="to-right"
                variant="outlined"
                :disabled="(solicitudes || []).length === 0"
              >
                Exportar detalle
              </v-btn>
            </template>
            <v-list>
              <v-list-item v-for="opt in itemsExportacion" :key="opt.value" density="compact" @click="onSelectExport(opt.value)">
                <v-list-item-title>{{ opt.label }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>
      </v-col>
    </v-row>
    <v-dialog
      v-if="modalCreateRecetaElectronica"
      v-model="modalCreateRecetaElectronica"
      :max-width="isMobile ? '100%' : '85%'"
      @keydown.esc="closeDialog(false)"
      persistent
    >
      <CreateRecetaElectronica
        :solicitudItem="solicitudItem"
        @closeModal="closeDialog($event)"
      />
    </v-dialog>
    <v-dialog
      v-if="modalCreatePrescripcionDispositivos"
      v-model="modalCreatePrescripcionDispositivos"
      :max-width="isMobile ? '100%' : '85%'"
      @keydown.esc="closeDialog(false)"
      persistent
    >
      <CreatePrescripcionDispositivos
        :solicitudItem="solicitudItem"
        @closeModal="closeDialog($event)"
      />
    </v-dialog>
    <v-dialog
      v-if="modalCreatePrescripcionEstudios"
      v-model="modalCreatePrescripcionEstudios"
      :max-width="isMobile ? '100%' : '85%'"
      @keydown.esc="closeDialog(false)"
      persistent
    >
      <CreatePrescripcionEstudios
        :solicitudItem="solicitudItem"
        @closeModal="closeDialog($event)"
      />
    </v-dialog>
    <v-dialog
      v-if="isOpenModalStatus"
      v-model="isOpenModalStatus"
      :max-width="isMobile ? '95%' : '70%'"
      persistent
      @keydown.esc="closeDialog(false)"
    >
      <estado-solicitud
        :solicitudItem="solicitudItem"
        @closeDialog="closeDialog($event)"
      />
    </v-dialog>
    <v-dialog
      v-if="verLogSolicitudDialog"
      v-model="verLogSolicitudDialog"
      :max-width="isMobile ? '95%' : '70%'"
      persistent
      @keydown.esc="closeDialog(false)"
    >
      <VerLogSolicitud
        :solicitudId="solicitudItem"
        :esAdmin="esAdmin"
        @closeDialog="closeDialog($event)"
      />
    </v-dialog>
  </v-container>
  <v-dialog
    :max-width="isMobile ? '95%' : '50%'"
    v-model="anularSolicitudDialog"
    persistent
    @keydown.esc="closeDialog(false)"
  >
    <AnularSolicitudes
      :solicitudItem="solicitudItem"
      @anular-cancelado="closeDialog($event)"
    />
  </v-dialog>
  <v-dialog
    v-if="isOpenModalReceta"
    v-model="isOpenModalReceta"
    :max-width="isMobile ? '95%' : '70%'"
    persistent
    @keydown.esc="closeDialog(false)"
  >
    <DispensarReceta
      :solicitudHash="solicitudItem.numero"
      :esFarmacia="isUserFarmacia"
      :financiadorId="solicitudItem?.financiador?.id"
      @closeDialog="closeDialog($event)"
    />
  </v-dialog>
</template>

<script>
import PageHeader from "@/components/layout/PageHeader.vue";
import enums from "@/utils/enums/index";
import { mapActions, mapGetters } from "vuex";
import rules from "@/utils/helpers/rules";
import helperDate from "@/utils/helpers/dateTimePicker";
import DatePicker from "@/components/shared/DatePicker.vue";
import EstadoSolicitud from "@/components/modules/solicitudes/EstadoSolicitud.vue";
import AnularSolicitudes from "@/components/modules/solicitudes/AnularSolicitudes.vue";
import DispensarReceta from "@/components/modules/solicitudes/DispensarReceta.vue";
import VerLogSolicitud from "@/components/modules/solicitudes/VerLogSolicitud.vue";
import CreateRecetaElectronica from "@/components/modules/solicitudes/CreateRecetaElectronica.vue";
import CreatePrescripcionDispositivos from "@/components/modules/solicitudes/CreatePrescripcionDispositivos.vue";
import CreatePrescripcionEstudios from "@/components/modules/solicitudes/CreatePrescripcionEstudios.vue";
import { focusComponent } from "@/utils/helpers/htmlHelpers";
import helpersExcel from "@/utils/helpers/importExportExcel.js";
// Mapa de códigos de TipoSolicitud prohibidos por rol (frontend)
// Claves: nombre lógico del rol en la app; valores: array de códigos (strings) que NO deben verse/consultarse
const TIPOS_PROH_POR_ROL = {
  farmacia: ["03"],
};

// Constantes para control de filtros y visibilidad de fechas por rol
const FECHAS_FILTRADAS_POR_ROL = {
  prestador: ["fechaEmision"],
  emisor: ["fechaEmision"],
  farmacia: ["fechaEmision", "fechaDispensa"],
  financiador: ["fechaEmision", "fechaDispensa"],
  admin: ["fechaEmision","fechaCreacion", "fechaDispensa"],
};

const FECHAS_VISIBLES_POR_ROL = { 
// Se asigna "fechaCreacion", "fechaEmision","fechaDispensa" según el rol
  prestador: ["fechaEmision"],
  emisor: ["fechaEmision"],
  farmacia: ["fechaEmision"],  
  financiador: ["fechaEmision","fechaDispensa"],  
  admin: ["fechaCreacion", "fechaEmision","fechaDispensa"],  
};

const FECHA_LABELS = {
  fechaEmision: "Prescripción",
  fechaDispensa: "Dispensa",
  fechaCreacion: "Creación",
};

// Constantes de exportación: tipos y mapeo por rol
const TIPOS_EXPORTACION = {
  POR_SOLICITUD: 'por_solicitud',
  POR_DISPENSA: 'por_dispensa'
};

// Límite de CUIRs antes de mostrar scroll en modal Ver CUIR
const LIMITE_CUIR_SIN_SCROLL = 3;

// Exportaciones visibles por rol (misma estructura que FECHAS_FILTRADAS_POR_ROL)
const EXPORTACION_POR_ROL = {
  admin: [ TIPOS_EXPORTACION.POR_DISPENSA, TIPOS_EXPORTACION.POR_SOLICITUD ],
  financiador: [ TIPOS_EXPORTACION.POR_DISPENSA ],
  emisor: [ TIPOS_EXPORTACION.POR_SOLICITUD ],
  prestador: [ TIPOS_EXPORTACION.POR_SOLICITUD ],
  farmacia: [ TIPOS_EXPORTACION.POR_SOLICITUD ]
};

// Mapa de metadata para optimizar lógica repetida de filtros de fecha
const DATE_FIELD_MAP = {
  fechaEmision: {
    from: 'selectedFechaDesde', 
    to: 'selectedFechaHasta',
    labelFrom: `Fecha ${FECHA_LABELS.fechaEmision.toLowerCase()}  desde`, 
    labelTo: `Fecha ${FECHA_LABELS.fechaEmision.toLowerCase()}  hasta`
  },
  fechaCreacion: {
    from: 'selectedFechaCreacionDesde', 
    to: 'selectedFechaCreacionHasta',
    labelFrom: `Fecha ${FECHA_LABELS.fechaCreacion.toLowerCase()}  desde`, 
    labelTo: `Fecha ${FECHA_LABELS.fechaCreacion.toLowerCase()}  hasta`
  },
  fechaDispensa: {
    from: 'selectedFechaDispensaDesde', 
    to: 'selectedFechaDispensaHasta',
    labelFrom: `Fecha ${FECHA_LABELS.fechaDispensa.toLowerCase()}  desde`, 
    labelTo: `Fecha ${FECHA_LABELS.fechaDispensa.toLowerCase()} hasta`, 
  }
};

// IDs lógicos para tipos de solicitud (centralizar valores mágicos)
const TIPO_SOLICITUD_IDS = {
  MEDICAMENTOS: 1,
  DISPOSITIVOS: 2,
  ESTUDIOS: 3,
};

// Configuración para exportar según tipo de solicitud
const EXPORT_CONFIG = {
  [TIPO_SOLICITUD_IDS.MEDICAMENTOS]: {
    prescriptos: 'medicamentosPreescriptos',
    dispensados: 'medicamentosDispensados',
    tipo: 'medicamentos',
  },
  [TIPO_SOLICITUD_IDS.DISPOSITIVOS]: {
    prescriptos: 'dispositivosPreescriptos',
    dispensados: 'dispositivosDispensados',
    tipo: 'dispositivos',
  },
  [TIPO_SOLICITUD_IDS.ESTUDIOS]: {
    prescriptos: 'estudiosPreescriptos',
    dispensados: 'estudiosDispensados',
    tipo: 'estudios',
  },
  default: {
    prescriptos: 'medicamentosPreescriptos',
    dispensados: 'medicamentosDispensados',
    tipo: 'medicamentos',
  }
};

// Mapa de permisos para simplificar el cómputo
const PERMISSION_MAP = {
  [enums.permises.VER_HISTORIAL.code]: 'canSeeHistory',
  [enums.permises.DISPENSAR_RECETA.code]: 'canSeeDispensarReceta',
  [enums.permises.VER_LOG.code]: 'canSeeLog',
  [enums.permises.ANULAR_RECETA.code]: 'canSeeCancelApplication',
  [enums.permises.CREAR_RECETA.code]: 'canCreateReceta',
};

// Estados de solicitud centralizados (posición fija 1..6)
const ESTADOS_SOLICITUD = [
  null,                           // índice 0 vacío para usar 1-based
  "PENDIENTE DE DISPENSA",        // 1
  "RECHAZADA",                    // 2
  "DISPENSADA PARCIALMENTE",      // 3
  "DISPENSADA",                   // 4
  "ANULADA",                      // 5
  "VENCIDA"                       // 6
];

// Helper para extraer el código de tipo de una estructura recibida de la API
const getTypeCode = (tipo) => (tipo && (tipo.code || tipo.Code || tipo.TipoSolicitud_codigo || tipo.TipoSolicitudCodigo || tipo.codigo || '')).toString();

const FLUID_MIN_WIDTH = 1000;
const FLUID_MAX_WIDTH = 1439;

export default {
  name: "SolicitudesView",
  components: {
    PageHeader,
    DatePicker,
    EstadoSolicitud,
    AnularSolicitudes,
    DispensarReceta,
    VerLogSolicitud,
    CreateRecetaElectronica,
    CreatePrescripcionDispositivos,
    CreatePrescripcionEstudios,
  },
  data() {
    return {
      pageTitle: enums.permises.PRESCRIPCIONES.name,
      goToPath: enums.permises.HOME.path,
      isFormValid: false,
      loadingCopyReceta: false,
      search: "",
      emisores: [],
      financiadores: [],
      afiliados: [],
      solicitudes: [],
      tiposSolicitudes: [],
      selectedTiposSolicitudes: [],
      appliedTiposSolicitudes: [],
      tiposSolicitudAllowedCodes: [],
      selectedEmisor: null,
      selectedFinanciador: null,
      selectedAfiliado: null,
      inputAfiliado: null,
      numeroSolicitud: null,
      searchIcon: enums.icons.SEARCH_ICON,
      clearFiltersIcon: enums.icons.CLEAR_FILTERS,
      anularSolicitudIcon: enums.icons.DOCUMENT_DELETE_ICON,
      imprimirSolicitudIcon: enums.icons.PRINT_ICON,
      showFilters: true,
      headers: [
        {
          title: "Número solicitud",
          key: "numero",
          sortable: false,
        },
        {
          title: "Fecha creación",
          key: "fechaCreacion",
          sortable: false,
        },
        {
          title: "Fecha prescripción",
          key: "fechaEmision",
          sortable: false,
        },
        {
          title: "Afiliado",
          key: "apeNomAfi",
          sortable: false,
        },
        {
          title: "Emisor",
          key: "emisor.value",
          sortable: false,
        },
        {
          title: "Financiador",
          key: "financiador.value",
          sortable: false,
        },
        {
          title: "Estado",
          key: "estado",
          sortable: false,
        },
        {
          title: "CUIR",
          key: "cuir",
          sortable: false,
          width: 56,
          class: 'col-cuir'
        },
        {
          title: "Acciones",
          key: "actions",
          align: "end",
          sortable: false,
        },
      ],
      isLoadingTable: false,
      calendarClockIcon: enums.icons.CALENDAR_CLOCK,
      documentIcon: enums.icons.DOCUMENT,
      filtersApplied: [],
      loadingAfiliados: false,
      rules: rules,
      menuFechaDesde: false,
      menuFechaCreacionDesde: false,
      menuFechaCreacionHasta: false,
      fechaCreacionDesde: null,
      fechaCreacionHasta: null,
      selectedFechaCreacionDesde: null,
      selectedFechaCreacionHasta: null,
      fechaDesde: null,
      selectedFechaDesde: null,
      menuFechaHasta: false,
      fechaHasta: null,
      selectedFechaHasta: null,
      selectedFechaDispensaDesde: null,
      fechaDispensaDesde: null,
      menuFechaDispensaDesde: false,
      selectedFechaDispensaHasta: null,
      fechaDispensaHasta: null,
      menuFechaDispensaHasta: false,
      calendarIcon: enums.icons.CALENDAR,
      disabledByNroSolicitud: false,
      minDate: "2001",
      isUserFinanciador: false,
      isUserEmisor: false,
      downArrowIcon: enums.icons.DOWN_ARROW_ICON,
      upArrowIcon: enums.icons.UP_ARROW_ICON,
      editIcon: enums.icons.DOCUMENT_EDIT_ICON,
      afiliadosNoDataText: "",
      isOpenModalStatus: false,
      isOpenModalReceta: false,
      anularSolicitudDialog: false,
      verLogSolicitudDialog: false,
      solicitudItem: null,
      isUserFarmacia: false,
      selectedEstados: [],
      estadosList: [],
      selectedFarmacia: null,
      farmacias: [],
      loadingFarmacias: false,
      createOptions: [],
      modalCreateRecetaElectronica: false,
      modalCreatePrescripcionDispositivos: false,
      modalCreatePrescripcionEstudios: false,
      loadingPrint: false,
      timerAfiliado: null,
      isUserPrestador: false,
      loadedView: false,
      copyIcon: enums.icons.COPY_ICON,
      esAdmin: false,
      selectedDateFilter: null,
      tooltipStates: {},
      solicitudCuir: null,
      isFluidRange: false,
      // Exportación
      selectedTipoExportacion: null,
      exportMenu: false,
      // Control de visibilidad de columna Farmacia solo tras Aplicar
      showFarmaciaColumn: false,
      // Datos para modal Ver CUIR con ítems
      itemsCuirData: {},
      loadingItemsCuir: {},
      ESTADOS_SOLICITUD: ESTADOS_SOLICITUD,
    };
  },
  computed: {
    // Rol actual derivado de flags de usuario
    rolActual() {
      if (this.esAdmin) return 'admin';
      if (this.isUserEmisor) return 'emisor';
      if (this.isUserFinanciador) return 'financiador';
      if (this.isUserFarmacia) return 'farmacia';
      if (this.isUserPrestador) return 'prestador';
      return null;
    },

    // Opciones legibles para el v-select basadas en la constante
    availableDateFilters() {
      const vals = (this.rolActual && FECHAS_FILTRADAS_POR_ROL[this.rolActual]) ? FECHAS_FILTRADAS_POR_ROL[this.rolActual] : [];
      return vals.map(v => ({ value: v, text: FECHA_LABELS[v] || v }));
    },

    labelActual() {
      return this.selectedDateFilter && FECHA_LABELS[this.selectedDateFilter]
        ? FECHA_LABELS[this.selectedDateFilter].toLowerCase()
        : '';
    },

    selectedDateFrom: {
      get() {
        switch (this.selectedDateFilter) {
          case 'fechaCreacion':
            return this.selectedFechaCreacionDesde ?? (this.fechaCreacionDesde ? helperDate.formatDate(this.fechaCreacionDesde) : null);
          case 'fechaDispensa':
            return this.selectedFechaDispensaDesde ?? (this.fechaDispensaDesde ? helperDate.formatDate(this.fechaDispensaDesde) : null);
          default:
            return this.selectedFechaDesde ?? (this.fechaDesde ? helperDate.formatDate(this.fechaDesde) : null);
        }
      },
      set(val) {
        const apply = (selName, dateName, handler) => {
          if (val && val.dateString) {
            handler.call(this, val);
          } else if (typeof val === 'string') {
            this[selName] = val;
          } else if (val instanceof Date) {
            this[dateName] = val;
            this[selName] = helperDate.formatDate(val);
          } else {
            this[selName] = null;
            this[dateName] = null;
          }
        };

        switch (this.selectedDateFilter) {
          case 'fechaCreacion':
            apply('selectedFechaCreacionDesde', 'fechaCreacionDesde', this.selectFechaCreacionDesde);
            break;
          case 'fechaDispensa':
            apply('selectedFechaDispensaDesde', 'fechaDispensaDesde', this.selectFechaDispensaDesde);
            break;
          default:
            apply('selectedFechaDesde', 'fechaDesde', this.selectFechaDesde);
        }
      }
    },

    selectedDateTo: {
      get() {
        switch (this.selectedDateFilter) {
          case 'fechaCreacion':
            return this.selectedFechaCreacionHasta ?? (this.fechaCreacionHasta ? helperDate.formatDate(this.fechaCreacionHasta) : null);
          case 'fechaDispensa':
            return this.selectedFechaDispensaHasta ?? (this.fechaDispensaHasta ? helperDate.formatDate(this.fechaDispensaHasta) : null);
          default:
            return this.selectedFechaHasta ?? (this.fechaHasta ? helperDate.formatDate(this.fechaHasta) : null);
        }
      },
      set(val) {
        const apply = (selName, dateName, handler) => {
          if (val && val.dateString) {
            handler.call(this, val);
          } else if (typeof val === 'string') {
            this[selName] = val;
          } else if (val instanceof Date) {
            this[dateName] = val;
            this[selName] = helperDate.formatDate(val);
          } else {
            this[selName] = null;
            this[dateName] = null;
          }
        };

        switch (this.selectedDateFilter) {
          case 'fechaCreacion':
            apply('selectedFechaCreacionHasta', 'fechaCreacionHasta', this.selectFechaCreacionHasta);
            break;
          case 'fechaDispensa':
            apply('selectedFechaDispensaHasta', 'fechaDispensaHasta', this.selectFechaDispensaHasta);
            break;
          default:
            apply('selectedFechaHasta', 'fechaHasta', this.selectFechaHasta);
        }
      }
    },
    isClearFechas() {
      return (
        this.selectedFechaDispensaDesde !== null &&
        this.selectedFechaDispensaHasta !== null
      );
    },
    isClearFechasDispensa() {
      return (
        this.selectedFechaDesde !== null && this.selectedFechaHasta !== null
      );
    },
    isCuirInput() {
      const v = (this.numeroSolicitud || '').trim();
      return /^[0-9]+$/.test(v) && v.length >= 20 && v.length <= 50;
    },
    shouldShowTipoPrescripcionColumn() {
      const selectedLength = this.appliedTiposSolicitudes?.length || 0;
      return selectedLength !== 1;
    },
    headersTable() {
      const baseHeaders = [...this.headers];
      // Ajuste dinámico del ancho de la columna 'Cuir' según dispositivo
      const idxCuir = baseHeaders.findIndex(h => h.key === 'cuir');
      if (idxCuir !== -1) {
        baseHeaders[idxCuir] = { ...baseHeaders[idxCuir], width: this.isMobile ? 100 : 56 };
      }
      // Filtrar columnas de fecha según FECHAS_VISIBLES_POR_ROL para el rol actual
      const fechaCols = (this.rolActual && FECHAS_VISIBLES_POR_ROL[this.rolActual]) ? FECHAS_VISIBLES_POR_ROL[this.rolActual] : [];

      const alwaysKeys = ['numero','apeNomAfi','emisor.value','financiador.value','estado','cuir','actions','tipoSolicitud.value'];

      const filtered = baseHeaders.filter(h => {
        if (h.key === 'fechaCreacion') return fechaCols.includes('fechaCreacion');
        if (h.key === 'fechaEmision') return fechaCols.includes('fechaEmision');
        // Nunca mostrar fechaDispensa en la tabla (aunque esté disponible como fecha filtrable por rol)
        if (h.key === 'fechaDispensa') return false;
        return alwaysKeys.includes(h.key) || h.key.includes('tipoSolicitud');
      });

      // Deshabilitada la inserción automática de la columna 'fechaDispensa' en la tabla
      // (mantener exportación intacta)
      // if (fechaCols.includes('fechaDispensa') && !filtered.find(h => h.key === 'fechaDispensa')) {
      //   const idxEmision = filtered.findIndex(h => h.key === 'fechaEmision');
      //   const nueva = { title: 'Fecha última dispensa', key: 'fechaDispensa', sortable: false };
      //   if (idxEmision !== -1) filtered.splice(idxEmision + 1, 0, nueva);
      //   else filtered.unshift(nueva);
      // }



      if (!this.shouldShowTipoPrescripcionColumn) return filtered;

      const insertAfterKey = "numero"; // "Número solicitud"
      const newColumn = {
        title: "Tipo prescripción",
        key: "tipoSolicitud.value",
        sortable: false,
      };

      const idx = filtered.findIndex((h) => h.key === insertAfterKey);
      if (idx !== -1) {
        filtered.splice(idx + 1, 0, newColumn);
      } else {
        filtered.unshift(newColumn);
      }
      return filtered;
    },
    shouldShowFarmaciaColumn() {
      // Solo roles que tienen filtro de farmacia en UI
      const puedeFiltrarFarmacia = this.isUserFinanciador || this.esAdmin;
      if (!puedeFiltrarFarmacia) return false;
      // Si no hay una farmacia seleccionada (ninguna) o si en el futuro se habilita multiple y no es exactamente 1
      const sel = this.selectedFarmacia;
      const esMultiple = Array.isArray(sel) ? sel.length !== 1 : false;
      const esNinguna = !sel;
      return esNinguna || esMultiple;
      
    },
    opcionesExportacion() {
      const rol = this.rolActual;
      return (rol && EXPORTACION_POR_ROL[rol]) ? EXPORTACION_POR_ROL[rol] : [TIPOS_EXPORTACION.POR_SOLICITUD];
    },
    itemsExportacion() {
      // Mapea a etiquetas legibles
      const map = {
        [TIPOS_EXPORTACION.POR_SOLICITUD]: 'Exportar detalle general',
        [TIPOS_EXPORTACION.POR_DISPENSA]: 'Exportar detalle para financiador'
      };
      return this.opcionesExportacion.map(v => ({ value: v, label: map[v] || v }));
    },
    isAplicarButtonDisabled() {
      // Para usuario farmacia: si no filtra por nroSolicitud ni CUIR, requerir seleccionado Afiliado y al menos 1 tipo
      const noFiltroPorSolCuir = !this.numeroSolicitud && !this.solicitudCuir;
      if (this.isUserFarmacia && noFiltroPorSolCuir) {
        const faltaAfiliado = !this.selectedAfiliado;

        return faltaAfiliado || !this.isFormValid;
      }

      // Requerir que exista al menos un filtro o que el form sea válido
      const noHayFiltrosBasicos = !this.selectedEmisor &&
        !this.selectedFinanciador &&
        !this.selectedAfiliado &&
        !this.numeroSolicitud &&
        !this.solicitudCuir &&
        !this.selectedFarmacia &&
        !(this.selectedTiposSolicitudes && this.selectedTiposSolicitudes.length > 0);

      // Si hay un filtro de fecha seleccionado, exigir al menos desde o hasta (no permitir ninguno vacío)
      const necesitaFecha = !!this.selectedDateFilter && !this.numeroSolicitud && !this.solicitudCuir;
      const faltaFecha = necesitaFecha && !this.selectedDateFrom && !this.selectedDateTo;

      return (noHayFiltrosBasicos || faltaFecha) || !this.isFormValid;
    },
    ...mapGetters({
      permisos: "menu/permisos",
      isMobile: "menu/isMobile",
    }),
    permisosSolicitudes() {
      const result = {
        canSeeHistory: false,
        canSeeLog: false,
        canSeeCancelApplication: false,
        canSeeDispensarReceta: false,
        canCreateReceta: false,
      };
      if (this.permisos != null) {
        this.permisos.forEach((p) => {
          const key = PERMISSION_MAP[p.permisoCodigo];
          if (key) result[key] = true;
        });
      }
      return result;
    },
  },
  watch: {
    inputAfiliado(val) {
      if ((val?.trim() && val?.trim().length >= 3) || this.selectedAfiliado) {
        this.findAfiliado();
      } else {
        this.afiliadosNoDataText =
          enums.messages.ENTER_MINIMUM_CARACTERS_SEARCH;
        this.afiliados = [];
      }
    },
  },
  created() {
    this.loadView();
    this.$store.dispatch("user/changeAppLoading", {
      status: false,
      text: "",
    });
  },
  mounted() {
    // Cerrar tooltips al hacer click en cualquier parte
    document.addEventListener('click', (e) => {
      if (!e.target.closest('[data-cuir-tooltip]')) {
        this.closeAllTooltips();
      }
    });
    // Inicializar y escuchar cambios de ancho para el modo fluid
    this.updateFluidRange();
    window.addEventListener('resize', this.updateFluidRange);
  },
  beforeUnmount() {
    // Limpiar el event listener
    document.removeEventListener('click', this.closeAllTooltips);
    window.removeEventListener('resize', this.updateFluidRange);
  },
  methods: {
    ...mapActions({
      getEmisores: "taxonomy/getEmisores",
      getFinanciadores: "taxonomy/getFinanciadores",
      getTiposSolicitudes: "taxonomy/getTiposSolicitudes",
      getSolicitudes: "solicitud/getSolicitudes",
      getFiltersByUser: "solicitud/getFiltersByUser",
      getEstados: "taxonomy/getEstados",
      getFarmaciasByFinanciador: "taxonomy/getFarmaciasByFinanciador",
      getFarmacias: "taxonomy/getFarmacias",
      printSolicitud: "solicitud/printReceta",
      copiarPrescripcionMedicamentos: "solicitud/copiarPrescripcionMedicamentos",
      copiarPrescripcionDispositivos: "solicitud/copiarPrescripcionDispositivos",
      copiarPrescripcionEstudios: "solicitud/copiarPrescripcionEstudios",
      setAlert: "user/setAlert",
      fetchAfiliadosSolicitudesByQuery:
        "taxonomy/fetchAfiliadosSolicitudesByQuery",
      // Nuevos métodos para operaciones por CUIR de ítems
      dispensarMedicamentosPorCuir: "solicitud/dispensarMedicamentosPorCuir",
      dispensarDispositivosPorCuir: "solicitud/dispensarDispositivosPorCuir", 
      dispensarEstudiosPorCuir: "solicitud/dispensarEstudiosPorCuir",
      printRecetaPorItemsCuir: "solicitud/printRecetaPorItemsCuir",
    }),
    updateFluidRange() {
      const width = window.innerWidth || document.documentElement.clientWidth;
      this.isFluidRange = width >= FLUID_MIN_WIDTH && width <= FLUID_MAX_WIDTH;
    },
    selectFechaDesde(event) {
      this.setDateField('fecha', true, event);
    },
    selectFechaCreacionDesde(event) {
      this.setDateField('fechaCreacion', true, event);
    },
    focusComponent(refName, timeout) {
      focusComponent(refName, timeout);
    },
    selectFechaHasta(event) {
      this.setDateField('fecha', false, event);
    },
    selectFechaCreacionHasta(event) {
      this.setDateField('fechaCreacion', false, event);
    },
    selectFechaDispensaDesde(event) {
      this.setDateField('fechaDispensa', true, event);
    },
    selectFechaDispensaHasta(event) {
      this.setDateField('fechaDispensa', false, event);
    },
    // Método genérico para asignar pares selectedFecha*/fecha* para distintos tipos
    setDateField(fieldBase, isFrom, event) {
      const capitalize = (s) => s.charAt(0).toUpperCase() + s.slice(1);
      const baseCap = capitalize(fieldBase);
      const selName = `selected${baseCap}${isFrom ? 'Desde' : 'Hasta'}`;
      const dateName = `${fieldBase}${isFrom ? 'Desde' : 'Hasta'}`;

      // Asignación para el campo que disparó el cambio
      this[selName] = event.dateString;
      this[dateName] = event.date;

      // Sincronizar siempre las otras parejas de fechas
      const bases = ['fecha', 'fechaCreacion', 'fechaDispensa'];
      bases.forEach((fb) => {
        const cap = capitalize(fb);
        const s = `selected${cap}${isFrom ? 'Desde' : 'Hasta'}`;
        const d = `${fb}${isFrom ? 'Desde' : 'Hasta'}`;
        this[s] = event.dateString;
        this[d] = event.date;
      });
    },
    async imprimirSolicitud(solicitudNumero) {
      this.loadingPrint = true;
      this.$store.dispatch("user/changeAppLoading", {
        status: true,
        text: "",
      });
      
      try {
        // Buscar la solicitud en la lista actual para obtener sus ítems
        const solicitud = this.solicitudes.find(s => s.numero === solicitudNumero);
        if (solicitud) {
          // Extraer CUIRs de ítems y usar el nuevo método
          const itemsCuir = this.extractItemsCuirFromSolicitud(solicitud);
          if (itemsCuir.length > 0) {
            // Convertir el array de CUIRs a string separado por comas para la API
            const itemsCuirString = itemsCuir.join(',');
            await this.printRecetaPorItemsCuir(itemsCuirString);
          } else {
            // Fallback al método anterior si no hay CUIRs de ítems
            await this.printSolicitud(solicitudNumero);
          }
        } else {
          // Fallback al método anterior si no encontramos la solicitud
          await this.printSolicitud(solicitudNumero);
        }
      } catch (error) {
        console.error('Error al imprimir solicitud:', error);
        // Fallback al método anterior en caso de error
        await this.printSolicitud(solicitudNumero);
      }
      
      this.$store.dispatch("user/changeAppLoading", {
        status: false,
        text: "",
      });
      this.loadingPrint = false;
    },
    async loadView() {
      this.emisores = await this.getEmisores();
      this.financiadores = await this.getFinanciadores();
      this.tiposSolicitudes = await this.getTiposSolicitudes();

      this.estadosList = await this.getEstados();

      await this.filtersByUser();

      // Si la API devolvió códigos permitidos para el usuario (p.ej. farmacia), filtrar los tipos
      if (this.isUserFarmacia && Array.isArray(this.tiposSolicitudAllowedCodes) && this.tiposSolicitudAllowedCodes.length > 0) {
        const allowed = this.tiposSolicitudAllowedCodes.map(c => c?.toString());
        this.tiposSolicitudes = (this.tiposSolicitudes || []).filter(t => allowed.includes((t.code || t.Code || '').toString()));
      }


      // Bajo ningún supuesto el usuario farmacia vea o reciba resultados de estudios.
      if (this.isUserFarmacia) {
        const forbiddenCodes = TIPOS_PROH_POR_ROL.farmacia || [];
        this.tiposSolicitudes = (this.tiposSolicitudes || []).filter(t => {
          const code = getTypeCode(t);
          return !forbiddenCodes.includes(code);
        });
      }

      this.setDefaultDates();

      // Construir opciones del menú de creación desde los tipos de prescripción de la API (siempre)
      this.createOptions = (this.tiposSolicitudes || []).map((x) => ({
        id: x.id,
        title: x.value,
      }));

      this.loadedView = true;
      focusComponent("v-text-field-nroSolicitud", 1);
      // Inicializar tipo exportación por defecto (primera opción visible por rol)
      this.selectedTipoExportacion = (this.opcionesExportacion && this.opcionesExportacion.length > 0)
        ? this.opcionesExportacion[0]
        : TIPOS_EXPORTACION.POR_SOLICITUD;
    },
    async filtersByUser() {
      try {
        const filters = await this.getFiltersByUser();
        this.tiposSolicitudAllowedCodes = (filters && filters.tiposSolicitudAllowedCodes) ? filters.tiposSolicitudAllowedCodes : [];
        if (filters && filters.esAdmin) {
          this.esAdmin = true;
          // Cargar todas las farmacias para administradores
          await this.loadAllFarmacias();
        }
        if (filters && filters.emisorId !== null) {
          this.isUserEmisor = true;
          this.selectedEmisor = this.emisores.find(
            (emisor) => emisor.id === filters.emisorId
          );
          if (
            filters.financiadoresxEmisor &&
            filters.financiadoresxEmisor.length > 0
          ) {
            this.financiadores = this.financiadores.filter((financiador) =>
              filters.financiadoresxEmisor.find((id) => id === financiador.id)
            );
          }
        } else if (filters && filters.financiadorId !== null) {
          this.isUserFinanciador = true;

          this.selectedFinanciador = this.financiadores.find(
            (financiador) => financiador.id === filters.financiadorId
          );
          if (
            filters.emisoresxFinanciador &&
            filters.emisoresxFinanciador.length > 0
          ) {
            this.emisores = this.emisores.filter((emisor) =>
              filters.emisoresxFinanciador.find((id) => id === emisor.id)
            );
          }
          // Cargar todas las farmacias del financiador
          await this.loadFarmaciasByFinanciador(filters.financiadorId);
        } else if (filters && filters.farmaciaId !== null) {
          this.isUserFarmacia = true;
        } else if (filters && filters.preId !== null) {
          this.isUserPrestador = true;
          if (
            filters.financiadoresXPrestador &&
            filters.financiadoresXPrestador.length > 0
          ) {
            this.financiadores = this.financiadores.filter((financiador) =>
              filters.financiadoresXPrestador.find(
                (id) => id === financiador.id
              )
            );
            if (this.financiadores.length === 1)
              this.selectedFinanciador = this.financiadores[0];
          }
        }
        // Determinar rolActual a partir de flags y setear selectedDateFilter al primer elemento permitido
        const rolActual = this.esAdmin ? 'admin' : this.isUserEmisor ? 'emisor' : this.isUserFinanciador ? 'financiador' : this.isUserFarmacia ? 'farmacia' : this.isUserPrestador ? 'prestador' : null;
        const available = (rolActual && FECHAS_FILTRADAS_POR_ROL[rolActual]) ? FECHAS_FILTRADAS_POR_ROL[rolActual] : [];
        this.selectedDateFilter = available.length > 0 ? available[0] : null;

      } catch (error) {
        console.error("Error al cargar los datos del usuario", error);
      }
    },
    async findAfiliado() {
      if (this.timerAfiliado) {
        clearTimeout(this.timerAfiliado);
        this.timerAfiliado = null;
      }
      this.timerPrestador = setTimeout(async () => {
        if (this.afiliados?.some((x) => x.title == this.inputAfiliado)) {
          return;
        } else if (!this.selectedAfiliado) {
          this.loadingAfiliados = true;
          const response = await this.fetchAfiliadosSolicitudesByQuery({
            input: this.inputAfiliado?.trim(),
          });
          this.afiliados = response;
          this.afiliadosNoDataText = enums.messages.AFILIADOS_NOT_FOUND;
          this.loadingAfiliados = false;
        }
      }, 1000);
    },
    async loadSolicitudes() {
      this.isLoadingTable = true;
      if (!this.isFormValid) {
        this.isLoadingTable = false;
        return;
      }
      this.showFilters = false;

      const { nroSol, cuirSol } = this.parseNumeroOCuir();
      this.computeAppliedTipos(nroSol, cuirSol);
      this.customizeFiltersApplied();

      const data = this.buildBasePayload(nroSol, cuirSol);
      data.tipoSolicitudIds = this.buildTipoSolicitudIds(nroSol, cuirSol);

      const ok = this.assignDatesToPayload(data, nroSol, cuirSol);
      if (!ok) {
        this.isLoadingTable = false;
        return;
      }

      const response = await this.getSolicitudes(data);

      this.solicitudes = response;
      // Calcular display de Farmacia para cada solicitud si corresponde
      if (Array.isArray(this.solicitudes)) {
        this.solicitudes.forEach(s => {
          s.farmacia = this.computeFarmaciasDisplay(s);
        });
      }
      this.isLoadingTable = false;
    },

    // Helpers privados para refactor
    parseNumeroOCuir() {
      let nroSol = null;
      let cuirSol = null;
      if (this.numeroSolicitud) {
        const isNumeric = /^[0-9]+$/.test(this.numeroSolicitud);
        if (isNumeric) {
          const len = this.numeroSolicitud.length;
          if (len >= 40 && len <= 50) {
            cuirSol = this.numeroSolicitud;
          } else {
            nroSol = parseFloat(this.numeroSolicitud);
          }
        }
      }
      return { nroSol, cuirSol };
    },

    computeAppliedTipos(nroSol, cuirSol) {
      if (nroSol || cuirSol) {
        this.appliedTiposSolicitudes = [];
      } else {
        this.appliedTiposSolicitudes = this.selectedTiposSolicitudes
          ? Array.isArray(this.selectedTiposSolicitudes)
            ? [...this.selectedTiposSolicitudes]
            : [this.selectedTiposSolicitudes]
          : [];
      }
    },

    buildBasePayload(nroSol, cuirSol) {
      return {
        emisorId: this.selectedEmisor ? this.selectedEmisor.id : null,
        financiadorId: this.selectedFinanciador ? this.selectedFinanciador.id : null,
        afiliado: this.selectedAfiliado ? this.selectedAfiliado.value : null,
        nroSolicitud: nroSol,
        solicitudCuir: cuirSol,
        estadoIds: this.selectedEstados.length != 0 ? this.selectedEstados.map((x) => x.id) : [],
        // fechas inicializadas en null, se asignan luego según el filtro seleccionado
        fechaDesde: null,
        fechaHasta: null,
        fechaCreacionDesde: null,
        fechaCreacionHasta: null,
        fechaDispensaDesde: null,
        fechaDispensaHasta: null,
        farmaciaId: this.selectedFarmacia ? this.selectedFarmacia.id : null,
      };
    },

    buildTipoSolicitudIds(nroSol, cuirSol) {
      if (this.selectedTiposSolicitudes && this.selectedTiposSolicitudes.length > 0 && !(nroSol || cuirSol)) {
        return this.selectedTiposSolicitudes.map((x) => x.id ?? x);
      }
      if (this.isUserFarmacia) {
        const forbiddenCodes = TIPOS_PROH_POR_ROL.farmacia || [];
        return (this.tiposSolicitudes || [])
          .filter((t) => {
            const code = getTypeCode(t);
            return !forbiddenCodes.includes(code);
          })
          .map((t) => t.id ?? t);
      }
      return [];
    },

    validateSelectedDateRange() {
      if (this.selectedDateFrom && this.selectedDateTo) {
        const validRange = rules.validDateRange(this.selectedDateFrom, this.selectedDateTo);
        if (Array.isArray(validRange) && validRange.length > 0) {
          this.setAlert({ type: 'error', message: 'Rango de fecha no válido' });
          return false;
        }
      }
      return true;
    },

    assignDatesToPayload(data, nroSol, cuirSol) {
      if (nroSol || cuirSol || !this.selectedDateFilter) return true;
      if (!this.validateSelectedDateRange()) return false;

      switch (this.selectedDateFilter) {
        case 'fechaCreacion':
          data.fechaCreacionDesde = this.selectedDateFrom ? helperDate.convertDateFormat(this.selectedDateFrom) : null;
          data.fechaCreacionHasta = this.selectedDateTo ? helperDate.convertDateFormat(this.selectedDateTo) : null;
          break;
        case 'fechaDispensa':
          data.fechaDispensaDesde = this.selectedDateFrom ? helperDate.convertDateFormat(this.selectedDateFrom) : null;
          data.fechaDispensaHasta = this.selectedDateTo ? helperDate.convertDateFormat(this.selectedDateTo) : null;
          break;
        default:
          data.fechaDesde = this.selectedDateFrom ? helperDate.convertDateFormat(this.selectedDateFrom) : null;
          data.fechaHasta = this.selectedDateTo ? helperDate.convertDateFormat(this.selectedDateTo) : null;
          break;
      }
      return true;
    },
    customizeFiltersApplied() {
      this.filtersApplied = [];
      if (this.selectedEmisor) {
        this.filtersApplied.push({
          key: "emisorNom",
          label: "Emisor",
          model: this.selectedEmisor?.value,
        });
      }
      if (this.selectedFinanciador) {
        this.filtersApplied.push({
          key: "financiadorNom",
          label: "Financiador",
          model: this.selectedFinanciador?.value,
        });
      }
      if (this.selectedAfiliado) {
        this.filtersApplied.push({
          key: "afiliadoNom",
          label: "Afiliado",
          model: `${this.selectedAfiliado?.title} - ${this.selectedAfiliado?.raw.value2}`,
        });
      }
      if (this.numeroSolicitud) {
        this.filtersApplied.push({
          key: "solicitudId",
          label: "Número solicitud",
          model: this.numeroSolicitud,
        });
      }
      if (this.solicitudCuir) {
        this.filtersApplied.push({
          key: "solicitudCuir",
          label: "Cuir",
          model: this.solicitudCuir,
        });
      }
      if (
        this.selectedEstados?.length != 0 &&
        this.selectedEstados?.length == this.estadosList?.length
      ) {
        this.filtersApplied.push({
          key: "estado",
          label: "Estado/s",
          model: "Todos",
        });
      } else if (this.selectedEstados?.length > 0) {
        this.filtersApplied.push({
          key: "estado",
          label: "Estado/s",
          model: this.selectedEstados.map((x) => x.value).join(", "),
        });
      }
      // Mostrar filtros de tipo basados en los tipos "aplicados" (los que realmente se enviaron en la consulta)
      if (this.appliedTiposSolicitudes?.length > 0) {
        this.filtersApplied.push({
          key: "tipoSolicitud",
          label: "Tipo",
          model: this.appliedTiposSolicitudes,
        });
      }
      // Mostrar fechas sólo si NO se está filtrando por número/CUIR
      const noFiltroPorSolCuir = !this.numeroSolicitud && !this.solicitudCuir;

      // Agregar filtros de fecha de forma optimizada usando DATE_FIELD_MAP
      if (noFiltroPorSolCuir && this.selectedDateFilter) {
        const meta = DATE_FIELD_MAP[this.selectedDateFilter];
        if (meta) {
          if (this[meta.from]) {
            this.filtersApplied.push({ key: meta.from.replace('selected', '').toLowerCase(), label: meta.labelFrom, model: this[meta.from] });
          }
          if (this[meta.to]) {
            this.filtersApplied.push({ key: meta.to.replace('selected', '').toLowerCase(), label: meta.labelTo, model: this[meta.to] });
          }
        }
      }
      if (this.selectedFarmacia) {
        this.filtersApplied.push({
          key: "farmacia",
          label: "Farmacia",
          model: this.selectedFarmacia?.value,
        });
      }
    },
    truncateText(text, maxLength = 20) {
      return (text ?? "")?.length > maxLength
        ? (text ?? "").substring(0, maxLength) + "..."
        : text;
    },
    getTextWhenFilterIsArray(currentFilter) {
      if (currentFilter.model.length > 0 && currentFilter.model.length <= 3) {
        return currentFilter.model.map((m) => {
          return m.value ? m.value : m;
        });
      }
      if (currentFilter.model.length > 3) {
        let totalFiltros = currentFilter.model.length - 3;
        let firstThree = currentFilter.model.slice(0, 3).map((f) => {
          return f.value ? f.value : f;
        });
        return `${firstThree} y ${totalFiltros} más`;
      }
    },
    clearFilters() {
      this.selectedEmisor = this.isUserEmisor ? this.selectedEmisor : null;
      this.selectedFinanciador = this.isUserFinanciador
        ? this.selectedFinanciador
        : null;
      this.inputAfiliado = null;
      this.selectedAfiliado = null;
      this.numeroSolicitud = null;
      this.solicitudCuir = null;
      this.selectedEstados = [];
      this.selectedTiposSolicitudes = [];
      this.appliedTiposSolicitudes = [];
      this.selectedFarmacia = null;
      this.fechaDispensaDesde = null;
      this.fechaDispensaHasta = null;
      this.disabledByNroSolicitud = false;
      this.setDefaultDates();
    },
    openModalCreateSolicitud(id) {
      // Recibe por parámetro el id del array de opciones para crear una solicitud
      // Ya que el id lo ponemos nosotros, hago un case para saber que modal abrir
      switch (id) {
        case TIPO_SOLICITUD_IDS.MEDICAMENTOS:
          this.modalCreateRecetaElectronica = true;
          break;
        case TIPO_SOLICITUD_IDS.DISPOSITIVOS:
          this.modalCreatePrescripcionDispositivos = true;
          break;
        case TIPO_SOLICITUD_IDS.ESTUDIOS:
          this.modalCreatePrescripcionEstudios = true;
          break;

        default:
          this.setAlert({
            type: "warning",
            message: "Esa función aún no está implementada.",
            timeout: 4000,
          });
          break;
      }
    },
    isClearable(filterType) {
      if (filterType === "emisor") {
        return this.selectedEmisor !== null && !this.isUserEmisor;
      } else if (filterType === "financiador") {
        return this.selectedFinanciador !== null && !this.isUserFinanciador;
      }
      return true;
    },
    openModalStatus(item) {
      this.solicitudItem = item;
      this.isOpenModalStatus = true;
    },
    canDispensarReceta(item) {
      return (
        (item.estado === ESTADOS_SOLICITUD[1] ||
          item.estado === ESTADOS_SOLICITUD[3] ||
          item.estado === ESTADOS_SOLICITUD[4]) &&
        new Date(helperDate.convertDateFormat(item.fechaEmision)) <= new Date()
      );
    },
    canImprimirReceta({ estado }) {
      return (
        estado === ESTADOS_SOLICITUD[1] ||
        estado === ESTADOS_SOLICITUD[3] ||
        estado === ESTADOS_SOLICITUD[4] ||
        estado === ESTADOS_SOLICITUD[5] ||
        estado === ESTADOS_SOLICITUD[6]
      );
    },
    canAnularReceta(item) {
      return (
        (this.isUserPrestador && item.estado == ESTADOS_SOLICITUD[1]) ||
        (this.isUserEmisor && item.estado != ESTADOS_SOLICITUD[5]) ||
        (this.permisosSolicitudes.canSeeCancelApplication &&
          item.estado != ESTADOS_SOLICITUD[5] &&
          item.estado === ESTADOS_SOLICITUD[1])
      );
    },
    async copyPrescripcion(item) {
      this.loadingCopyReceta = true;
      this.$store.dispatch("user/changeAppLoading", {
        status: true,
        text: "",
      });
      try {
        const tipoSolicitudId = item.tipoSolicitud?.id;
        let response;
        
        switch (tipoSolicitudId) {
          case 1: // Receta electrónica
            response = await this.copiarPrescripcionMedicamentos(item.id);
            break;
          case 2: // Prescripción de dispositivos
            response = await this.copiarPrescripcionDispositivos(item.id);
            break;
          case 3: // Prescripción de estudios complementarios
            response = await this.copiarPrescripcionEstudios(item.id);
            break;
          default:
            this.setAlert({
              type: "warning",
              message: "Tipo de prescripción no soportado para copiar.",
              timeout: 4000,
            });
            return;
        }
        
        if (response.status === 200) {
          // Primero asignar los datos de la solicitud al prop, luego abrir el modal
          // para que el componente hijo reciba la prop ya inicializada.
          this.solicitudItem = response.data;
          this.openModalCreateSolicitud(tipoSolicitudId);
        }
      } catch (error) {
        console.error(error);
      }
      this.$store.dispatch("user/changeAppLoading", {
        status: false,
        text: "",
      });
      this.loadingCopyReceta = false;
    },
    openModalReceta(item) {
      this.isOpenModalReceta = true;
      this.solicitudItem = item;
    },
    openModalVerLog(id) {
      this.verLogSolicitudDialog = true;
      this.solicitudItem = id;
    },
    customFilter(item) {
      return item.value3 || item.value1 || item.value2;
    },
    closeDialog(reload) {
      this.isOpenModalStatus = false;
      this.isOpenModalReceta = false;
      this.modalCreateRecetaElectronica = false;
      this.modalCreatePrescripcionDispositivos = false;
      this.modalCreatePrescripcionEstudios = false;
      this.anularSolicitudDialog = false;
      this.verLogSolicitudDialog = false;
      this.solicitudItem = null;
      if (reload) this.loadSolicitudes();
    },
    formatDate(date) {
      return helperDate.formatDate(date);
    },
    setDefaultDates() {
      const currentDate = new Date();
      const defaultStartDate = new Date(currentDate);
      defaultStartDate.setMonth(defaultStartDate.getMonth() - 1);

      // Inicializar las tres parejas de fechas para preservar valores al cambiar el filtro
      this.fechaDesde = defaultStartDate;
      this.fechaHasta = currentDate;

      this.fechaCreacionDesde = defaultStartDate;
      this.fechaCreacionHasta = currentDate;

      this.fechaDispensaDesde = defaultStartDate;
      this.fechaDispensaHasta = currentDate;

      // También inicializar los strings seleccionados para mantener consistencia
      this.selectedFechaDesde = helperDate.formatDate(defaultStartDate);
      this.selectedFechaHasta = helperDate.formatDate(currentDate);
      this.selectedFechaCreacionDesde = helperDate.formatDate(defaultStartDate);
      this.selectedFechaCreacionHasta = helperDate.formatDate(currentDate);
      this.selectedFechaDispensaDesde = helperDate.formatDate(defaultStartDate);
      this.selectedFechaDispensaHasta = helperDate.formatDate(currentDate);
    },
    onChangeTipoExportacion() {
      // No hacer nada extra; se usa al confirmar selección si se requiere
    },
    exportarConsulta(tipoExportacion = TIPOS_EXPORTACION.POR_SOLICITUD) {
      // Determinar el nombre de las columnas según el filtro aplicado
      let tipoColumna = "Items";
      const tiposAplicados = this.appliedTiposSolicitudes?.length || 0;
      
      if (tiposAplicados === 1) {
        const tipoId = this.appliedTiposSolicitudes[0]?.id;
        switch (tipoId) {
          case 1:
            tipoColumna = "Medicamentos";
            break;
          case 2:
            tipoColumna = "Dispositivos";
            break;
          case 3:
            tipoColumna = "Estudios";
            break;
          default:
            tipoColumna = "Items";
            break;
        }
      }

      let result = [];
      const tipoExp = tipoExportacion;
      
      // Determinar si mostrar columnas relacionadas con dispensa
      const showDispensaColumns = (this.solicitudes || []).some(s =>
        s.estado === ESTADOS_SOLICITUD[4] || s.estado === ESTADOS_SOLICITUD[3]
      );
      
      this.solicitudes?.forEach((x) => {
        // Función helper para formatear items prescriptos/dispensados
        const formatearItems = (items, tipo) => {
          if (!Array.isArray(items) || items.length === 0) {
            return "";
          }

          return items
            .map((item) => {
              let nombre, cantidad;
              
              if (tipo === 'medicamentos') {
                nombre = item.regNom;
                cantidad = item.cantidad;
              } else if (tipo === 'dispositivos') {
                nombre = item.dispositivo;
                cantidad = 1; // Los dispositivos no tienen cantidad específica
              } else if (tipo === 'estudios') {
                nombre = item.estudio;
                cantidad = item.cantidad;
              }

              // Formato más compacto: eliminar espacios innecesarios y usar formato más corto
              const nombreCorto = `● ${(nombre || 'Sin nombre')}`.trim();
              const cantidadStr = cantidad || 1;
              return `${nombreCorto} / (${cantidadStr})`;
            })
            .join("\n");
        };

        // Determinar qué datos mostrar según el tipo de solicitud
        let prescriptosFormatted = "";
        let dispensadosFormatted = "";

        const tipoSolicitudId = x.tipoSolicitud?.id;

        const config = EXPORT_CONFIG[tipoSolicitudId] || EXPORT_CONFIG.default;
        prescriptosFormatted = formatearItems(x[config.prescriptos], config.tipo);
        dispensadosFormatted = formatearItems(x[config.dispensados], config.tipo);

        // Determinar columnas exportables según rol
        const fechaColsExport = (this.rolActual && FECHAS_VISIBLES_POR_ROL[this.rolActual]) ? FECHAS_VISIBLES_POR_ROL[this.rolActual] : [];

        // Sólo incluir la columna Fecha dispensa en la exportación si el rol la tiene habilitada
        // y además existen solicitudes con estados de dispensa (parcial o total)
        const includeFechaDispensaInExport = fechaColsExport.includes('fechaDispensa') && showDispensaColumns;

        if (tipoExp === TIPOS_EXPORTACION.POR_DISPENSA) {
          // Fila por item dispensado (medicamentos/dispositivos/estudios); si no hay, fila por solicitud con columnas de dispensa vacías
          const dispArray = Array.isArray(x[config.dispensados]) ? x[config.dispensados] : [];
          if (dispArray.length > 0) {
            dispArray.forEach(d => {
              const itemPrescripto = (prescriptosFormatted || '').trim();
              
              // Determinar nombre del item y cantidad según el tipo (medicamentos/dispositivos/estudios)
              let itemDispensado = '';
              let cantidadDispensada = '';
              if (config.tipo === 'medicamentos') {
                itemDispensado = (d.regNom || '').trim();
                cantidadDispensada = d.cantidad || '';
              } else if (config.tipo === 'dispositivos') {
                itemDispensado = (d.dispositivo || '').trim();
                cantidadDispensada = d.cantidad || 1;
              } else if (config.tipo === 'estudios') {
                itemDispensado = (d.estudio || '').trim();
                cantidadDispensada = d.cantidad || '';
              }

              const farmaciaNom = d.farmaciaNombre && d.farmaciaCuit ? `${d.farmaciaNombre} (${d.farmaciaCuit})` : '';
              const fila = {
                ["N° solicitud"]: x.numero,
                ["Tipo prescripción"]: x.tipoSolicitud?.value || "",
                ...(fechaColsExport.includes('fechaCreacion') && { ["Fecha creación"]: x.fechaCreacion }),
                ...(fechaColsExport.includes('fechaEmision') && { ["Fecha prescripción"]: x.fechaEmision }),
                ...(includeFechaDispensaInExport && { ["Fecha dispensa"]: (d.fechaDispensa ? this.formatFechaParaExport(d.fechaDispensa) : this.formatFechaParaExport(x.fechaDispensa)) }),
                ["Emisor"]: x.emisor.value,
                ["Financiador"]: x.financiador.value,
                ...(this.shouldShowFarmaciaColumn && showDispensaColumns && { ["Farmacia (CUIT)"]: farmaciaNom }),
                ["Estado"]: x.estado,
                [`${tipoColumna} prescriptos / (Cantidad)`]: itemPrescripto,
                ...(showDispensaColumns && { [`${tipoColumna} dispensados`]: itemDispensado }),
                ...(showDispensaColumns && { ["Cantidad dispensada"]: cantidadDispensada }),
                ["N° afiliado"]: x.nroAfi,
                ["Afiliado"]: x.apeNomAfi,
                ["Email"]: x.afiliadoContactInfo.email,
                ["Teléfono"]: x.afiliadoContactInfo.telefono,
                ["CUIR"]: this.extractItemsCuirFromSolicitud(x).join(', ') || "",
              };
              result.push(fila);
            });
          } else {
            // Sin dispensas: fila por solicitud con columnas de dispensa vacías (Farmacia vacía)
            const fila = {
              ["N° solicitud"]: x.numero,
              ["Tipo prescripción"]: x.tipoSolicitud?.value || "",
              ...(fechaColsExport.includes('fechaCreacion') && { ["Fecha creación"]: x.fechaCreacion }),
              ...(fechaColsExport.includes('fechaEmision') && { ["Fecha prescripción"]: x.fechaEmision }),
              ...(includeFechaDispensaInExport && { ["Fecha dispensa"]: this.formatFechaParaExport(x.fechaDispensa) || '' }),
              ["Emisor"]: x.emisor.value,
              ["Financiador"]: x.financiador.value,
              ...(this.shouldShowFarmaciaColumn && showDispensaColumns && { ["Farmacia (CUIT)"]: '' }),
              ["Estado"]: x.estado,
              [`${tipoColumna} prescriptos / (Cantidad)`]: (prescriptosFormatted || '').trim(),
              ...(showDispensaColumns && { [`${tipoColumna} dispensados`]: '' }),
              ...(showDispensaColumns && { ["Cantidad dispensada"]: '' }),
              ["N° afiliado"]: x.nroAfi,
              ["Afiliado"]: x.apeNomAfi,
              ["Email"]: x.afiliadoContactInfo.email,
              ["Teléfono"]: x.afiliadoContactInfo.telefono,
              ["CUIR"]: this.extractItemsCuirFromSolicitud(x).join(', ') || "",
            };
            result.push(fila);
          }
        } else {
          // por_solicitud (general): fila por solicitud (comportamiento anterior) + Farmacia si aplica
          const fila = {
            ["N° solicitud"]: x.numero,
            ["Tipo prescripción"]: x.tipoSolicitud?.value || "",
            ...(fechaColsExport.includes('fechaCreacion') && { ["Fecha creación"]: x.fechaCreacion }),
            ...(fechaColsExport.includes('fechaEmision') && { ["Fecha prescripción"]: x.fechaEmision }),
            ...(includeFechaDispensaInExport && { ["Fecha dispensa"]: this.formatFechaParaExport(x.fechaDispensa) }),
            ["Emisor"]: x.emisor.value,
            ["Financiador"]: x.financiador.value,
            ...(this.shouldShowFarmaciaColumn && showDispensaColumns && { ["Farmacia (CUIT)"]: x.farmacia || '' }),
          ["Estado"]: x.estado,
          [`${tipoColumna} prescriptos / (Cantidad)`]: prescriptosFormatted,
          ...(showDispensaColumns && { [`${tipoColumna} dispensados / (Cantidad)`]: dispensadosFormatted }),
          ["N° afiliado"]: x.nroAfi,
          ["Afiliado"]: x.apeNomAfi,
          ["Email"]: x.afiliadoContactInfo.email,
          ["Teléfono"]: x.afiliadoContactInfo.telefono,
          ["CUIR"]: this.extractItemsCuirFromSolicitud(x).join(', ') || "",
        };
        result.push(fila);
        }
      });

      let formato = {
        filas: result,
        hoja: "Recetas",
      };
      
      // Configurar anchos específicos para columnas de ítems
      const columnWidths = {};
      result.length > 0 && Object.keys(result[0]).forEach(key => {
        const keyLower = key.toLowerCase();
        if (keyLower.includes("prescriptos") || keyLower.includes("dispensados")) {
          columnWidths[key] = 80; // Ancho específico para columnas de ítems
        }
      });
      
      const exportOptions = {
        maxColumnWidth: 100,
        columnWidths: columnWidths
      };
      
      helpersExcel.excelExport(formato, "Consulta de solicitudes", exportOptions);
    },
    // openExportMenu ya no es necesario: renderizamos botón o menú según cantidad de opciones
    onSelectExport(tipo) {
      this.exportMenu = false;
      this.$nextTick(() => {
        this.exportarConsulta(tipo);
      });
    },
    formatFechaParaExport(fecha) {
      if (!fecha) return '';
      // Si ya es string en formato dd/MM/yyyy
      if (typeof fecha === 'string') {
        const re = /^\d{2}\/\d{2}\/\d{4}$/;
        if (re.test(fecha)) return fecha;
        // Si es ISO u otro string, intentar parsear
        const d = new Date(fecha);
        if (!isNaN(d.getTime())) return helperDate.formatDate(d);
        return fecha; // fallback: devolver tal cual
      }
      // Si es Date
      if (fecha instanceof Date) return helperDate.formatDate(fecha);
      // Fallback
      try {
        const d = new Date(fecha);
        if (!isNaN(d.getTime())) return helperDate.formatDate(d);
      } catch (e) { void 0; }
      return '';
    },
    async changeNumeroSolicitud() {
      if (this.numeroSolicitud) {
        // Si se busca por número, desactivar filtros por fecha en la UI, sin borrar sus valores
        this.selectedDateFilter = null;
        this.disabledByNroSolicitud = true;
        // Limpiar filtros no relacionados a fechas
        this.selectedEmisor =
          this.selectedFinanciador =
          this.selectedFarmacia =
          this.inputAfiliado =
          this.selectedAfiliado =
            null;
        // Limpiar tipos para evitar filtros residuales
        this.selectedTiposSolicitudes = [];
        this.appliedTiposSolicitudes = [];
        this.selectedEstados = [];
      } else {
        // Al borrar el número/CUIR, reactivar filtros y restaurar el tipo de fecha por rol
        this.disabledByNroSolicitud = false;
        this.solicitudCuir = null;
        await this.filtersByUser();
        // Si no hay fechas seleccionadas para el tipo actual, reinstalar por defecto
        if (!this.selectedDateFrom && !this.selectedDateTo) {
          this.setDefaultDates();
        }
      }
    },
    setAfiliado(afiliado) {
      this.selectedAfiliado = afiliado;
      this.inputAfiliado = afiliado.title;
    },
    openAnularSolicitudDialog(item) {
      this.anularSolicitudDialog = true;
      this.solicitudItem = item;
    },
    
    closeAllTooltips() {
      this.tooltipStates = {};
    },
    
    closeTooltip(itemId) {
      this.tooltipStates[itemId] = false;
    },
    
    async copyCuir(cuir) {
      try {
        await navigator.clipboard.writeText(cuir);
        this.setAlert({ type: "success", message: "CUIR copiado al portapapeles" });
      } catch (err) {
        // Fallback para navegadores que no soportan clipboard API
        const textArea = document.createElement('textarea');
        textArea.value = cuir;
        document.body.appendChild(textArea);
        textArea.select();
        try {
          document.execCommand('copy');
          this.setAlert({ type: "success", message: "CUIR copiado al portapapeles" });
        } catch (fallbackErr) {
          this.setAlert({ type: "error", message: "No se pudo copiar el CUIR" });
        }
        document.body.removeChild(textArea);
      }
    },
    
    // Verifica si la cantidad de CUIRs supera el límite para mostrar scroll
    tieneMasQueLimiteCuir(item) {
      return (this.itemsCuirData[item.id] || []).length > LIMITE_CUIR_SIN_SCROLL;
    },
    
    async loadFarmaciasByFinanciador(financiadorId) {
      this.loadingFarmacias = true;
      try {
        this.farmacias = await this.getFarmaciasByFinanciador(financiadorId);
      } catch (error) {
        console.error("Error al cargar farmacias del financiador", error);
      } finally {
        this.loadingFarmacias = false;
      }
    },
    async loadAllFarmacias() {
      this.loadingFarmacias = true;
      try {
        this.farmacias = await this.getFarmacias();
      } catch (error) {
        console.error("Error al cargar todas las farmacias", error);
      } finally {
        this.loadingFarmacias = false;
      }
    },
    computeFarmaciasDisplay(solicitud) {
      try {
        const farmaciasSet = new Set();
        const pushFarmacia = (nombre, cuit) => {
          const nom = (nombre || '').trim();
          const cu = (cuit || '').trim();
          if (!nom && !cu) return;
          farmaciasSet.add(`${nom}${nom && cu ? ' ' : ''}${cu ? '(' + cu + ')' : ''}`);
        };
        // Medicamentos
        (solicitud?.medicamentosDispensados || []).forEach(md => pushFarmacia(md.farmaciaNombre, md.farmaciaCuit));
        // Dispositivos
        (solicitud?.dispositivosDispensados || []).forEach(d => pushFarmacia(d.farmaciaNombre, d.farmaciaCuit));
        // Estudios
        (solicitud?.estudiosDispensados || []).forEach(e => pushFarmacia(e.farmaciaNombre, e.farmaciaCuit));
        return Array.from(farmaciasSet).join('; ');
      } catch {
        return '';
      }
    },
    
    // Métodos para manejo de CUIRs de ítems
    async loadItemsCuir(item) {
      if (this.itemsCuirData[item.id]) {
        // Ya están cargados, no hacer nada
        return;
      }
      
      // Marcar carga (Vue 3: evitar this.$set)
      this.loadingItemsCuir[item.id] = true;
      
      try {
        // Obtener los CUIRs de los ítems de la prescripción
        const itemsCuir = this.extractItemsCuirFromSolicitud(item);
        // Asignar datos de CUIRs por solicitud
        this.itemsCuirData[item.id] = itemsCuir;
      } catch (error) {
        console.error('Error al cargar CUIRs de ítems:', error);
        this.setAlert({ type: 'error', message: 'Error al cargar los CUIRs de los ítems' });
        this.itemsCuirData[item.id] = [];
      } finally {
        this.loadingItemsCuir[item.id] = false;
      }
    },
    
    extractItemsCuirFromSolicitud(solicitud) {
      const cuirs = [];

      const pushIf = (val) => {
        if (val === null || val === undefined) return;
        const t = String(val).trim();
        if (t) cuirs.push(t);
      };

      // Preescriptos (orden de la receta)
      if (solicitud.medicamentosPreescriptos && Array.isArray(solicitud.medicamentosPreescriptos)) {
        solicitud.medicamentosPreescriptos.forEach(med => pushIf(med.cuir));
      }
      if (solicitud.dispositivosPreescriptos && Array.isArray(solicitud.dispositivosPreescriptos)) {
        solicitud.dispositivosPreescriptos.forEach(disp => pushIf(disp.cuir));
      }
      if (solicitud.estudiosPreescriptos && Array.isArray(solicitud.estudiosPreescriptos)) {
        solicitud.estudiosPreescriptos.forEach(est => pushIf(est.cuir));
      }

      // Dispensados (también considerar CUIRs que sólo existen en logs de dispensa)
      if (solicitud.medicamentosDispensados && Array.isArray(solicitud.medicamentosDispensados)) {
        solicitud.medicamentosDispensados.forEach(med => pushIf(med.cuir));
      }
      if (solicitud.dispositivosDispensados && Array.isArray(solicitud.dispositivosDispensados)) {
        solicitud.dispositivosDispensados.forEach(disp => pushIf(disp.cuir));
      }
      if (solicitud.estudiosDispensados && Array.isArray(solicitud.estudiosDispensados)) {
        solicitud.estudiosDispensados.forEach(est => pushIf(est.cuir));
      }

      // Eliminar duplicados preservando orden
      return Array.from(new Set(cuirs));
    },
  },
};
</script>

<style scoped>
.cuir-details-card {
  font-family: 'Courier New', monospace;
  box-shadow: 0 8px 24px rgba(0,0,0,0.4) !important;
  border: 1px solid rgba(255,255,255,0.1);
}

.cuir-details-card .v-card-title {
  border-bottom: 1px solid rgba(255,255,255,0.1);
}

.cursor-pointer {
  cursor: pointer !important;
}

.cursor-pointer:hover {
  opacity: 0.8;
  transform: scale(1.05);
  transition: all 0.2s ease;
}

.cuir-text-clickable {
  user-select: none !important;
  -webkit-user-select: none !important;
  -moz-user-select: none !important;
  -ms-user-select: none !important;
}

.cuir-text-clickable:hover {
  background: rgba(255,255,255,0.2) !important;
  transform: scale(1.02);
  box-shadow: 0 2px 8px rgba(52, 152, 219, 0.3);
}

.cuir-text-clickable:hover .v-icon {
  opacity: 1 !important;
  transform: scale(1.1);
}

.cuir-text-clickable:active {
  transform: scale(0.98);
  background: rgba(255,255,255,0.25) !important;
}

.cuir-item-clickable {
  user-select: none !important;
  -webkit-user-select: none !important;
  -moz-user-select: none !important;
  -ms-user-select: none !important;
}

.cuir-item-clickable:hover {
  background: rgba(255,255,255,0.15) !important;
  transform: scale(1.01);
  box-shadow: 0 1px 4px rgba(52, 152, 219, 0.2);
}

.cuir-item-clickable:hover .v-icon {
  opacity: 1 !important;
  transform: scale(1.1);
}

.cuir-item-clickable:active {
  transform: scale(0.99);
  background: rgba(255,255,255,0.2) !important;
}

/* Scroll para lista de CUIRs cuando supera el límite */
.cuir-list-scroll {
  max-height: 200px;
  overflow-y: auto;
  padding-right: 8px;
}

.table-compact {
  font-size: 12px;
}
.table-compact .v-data-table__th {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: -0.1px;
}
.table-compact .v-data-table__th,
.table-compact .v-data-table__td {
  padding-left: 6px !important;
  padding-right: 6px !important;
  line-height: 1.1;
  white-space: nowrap;
}

/* Estilos para columna CUIR (ancho reducido) - alineado al centro, derecha en mobile */
.col-cuir .v-data-table__td,
.col-cuir .v-data-table__th {
  text-align: center;
  padding-left: 6px !important;
  padding-right: 6px !important;
  width: 56px !important;
  max-width: 56px !important;
}

/* Alineación derecha para CUIR en mobile */
@media (max-width: 600px) {
  .col-cuir .v-data-table__td,
  .col-cuir .v-data-table__th {
    text-align: right;
  }
}
</style>
